import Vue from 'vue'
import Vuex from 'vuex'
import createPersistedState from "vuex-persistedstate";
import moment from "moment"
import constants from '../helpers/constants';

Vue.use(Vuex)

export default new Vuex.Store({
  plugins: [createPersistedState({
    key: "seatbooking.vuex",
    reducer: (persistedState) => {
      const stateFilter = Object.assign({}, persistedState)
      const blackList = ['time', 'loading', 'result', 'systemAlert', 'systemDialog']

      blackList.forEach((item) => {
        delete stateFilter[item]
      })

      return stateFilter
    }
  })],
  state: {
    time: null,             //Single source of truth of current time
    settings: null,         //settings (Type = PublicAppSettings) is returned from server when page refresh and first login. Without it almost nothing can run
    accountInfo: null,      //Only available after patron login
    showLoginPage: false,   //For testing, local login page
    showNavBar: true,       //Only show bottom navigation bar on the first level pages
    showQRCodeBar: true,
    showCheckIn: true,      //Hide the floating check in button from certain pages, e.g. ScanQRCode page
    navBarActiveButton: 0,  //Ensure the button on the bottom navigation bar is highlighted correctly
    navQRCodeActiveButton: 0,
    otpTimerStartTime: null, //seperate so will not be reset
    otpTimer: {
      timerInSeconds: 0,
      progressPercentage: 100,
      timerInterval: {},
      isLoading: false,
    },
    loading: {              //To control <Loading/> component
      show: false,
      notRevertStatusBarColor: false
    },
    result: {               //To control <RippleResult/> component that shows transaction results with ripple animation effect
      show: false,
      success: true,
      title: "",
      message: ""
    },
    systemAlert: {          //To control <SystemAlert/> component
      show: false,
      success: true,
      message: ""
    },
    systemDialog: null,     //To control <SystemDialog/> component
    // systemDialog: {
    //   title: "",
    //   message: "",
    //   persistentDialog: false,
    //   actions: {
    //     actionType: "", //"YesOrNo" or only yes
    //     yes: {
    //       label: "",
    //       url: ""
    //     }
    //   }
    // },
    qrCode: { value: null, state: null },   //QR code scanning result will be stored in the value field, while state field tells which component initiated the QR scanning and it has to handled the result
    locationInfoFromQrCode: null,           //Area identifiable, seat identifiable is optional. E.g. {"instituteCode":"NLB","placeCode":"TRL.8.BloombergTerminal","branchCode":"TRL","floor":"8","seatNo":"2","seatCode":"TRL.8.BloombergTerminal.2","branchId":352,"time":"2021-05-09T11:09:52.417Z"}
    locationInfoFromGps: null,              //Type = PublicBranchMenu, branch identifiable
    locationInfoFromUrl: null,
    //sid: "",
    searchResults: null,                    //Stored it to survive page reload
    selectedArea: null,                     //Stored it to survive page reload
    selectedBooking: null,                  //Stored it to survive page reload
    visitBookingSearchResult: null,         //Stored it to survive page reload
    preCasAuthState: null,
    qrCodeEntry: "",
    casAuthState: "",
  },
  getters: {
    authenticated: (state, getters) => {
      return !!state.accountInfo
    },
    accountType: (state, getters) => {
      if (getters.authenticated) {
        return state.accountInfo.accountType;
      }
      return null;
    },
    // TODO: THink about it && (a || b) [DONE]
    isEmailMobileLogin: (state, getters) => {
      if (getters.accountType && (getters.visitBookingEnabled || getters.gantryAccessQrEnabled)) { //TODO: Remove getters.visitBookingEnabled)
        return getters.accountType.includes(constants.accountType.EmailLoginUser) || getters.accountType.includes(constants.accountType.MobileLoginUser);
      }
      return false;
    },
    initialized: (state, getters) => {
      return !!state.settings;
    },
    onsiteMode: (state, getters) => {
      return getters.mode && (getters.mode.mode === constants.mode.OnsiteModeGps || getters.mode.mode === constants.mode.OnsiteModeQrCode)
    },
    mode: (state, getters) => {
      //Mode tells which mode and where
      const mode = {
        mode: constants.mode.OffsiteMode,
        branchId: 0,
        branchCode: null,
        placeCode: null,
        seatCode: null
      };

      if (state.settings && state.time && state.time.nowMoment && typeof state.time.nowMoment === "object") {
        const locationTimeout = state.settings.system.onsiteModeTimeoutInMinutes;

        if (state.locationInfoFromQrCode) {
          //With QR code area and seat (optional) can be determined
          const diffMinutes = state.time.nowMoment.diff(moment(state.locationInfoFromQrCode.time), "minutes")

          if (diffMinutes < locationTimeout) {
            mode.mode = constants.mode.OnsiteModeQrCode
            mode.branchId = state.locationInfoFromQrCode.branchId
            mode.placeCode = state.locationInfoFromQrCode.placeCode
            mode.seatCode = state.locationInfoFromQrCode.seatCode

            return mode
          }
        }

        if (state.locationInfoFromGps) {
          //With GPS only branch can be determined
          //locationInfoFromGps Type = PublicBranchMenu
          const diffMinutes = state.time.nowMoment.diff(moment(state.locationInfoFromGps.time), "minutes")

          if (diffMinutes < locationTimeout) {
            mode.mode = constants.mode.OnsiteModeGps
            mode.branchId = state.locationInfoFromGps.id
            mode.branchCode = state.locationInfoFromGps.code
            mode.placeCode = null
            mode.seatCode = null

            return mode
          }
        }
      }

      return mode;
    },
    seatBranchMenus: (state, getters) => {
      return state.settings
        ? state.settings.menus.branchMenus.filter(x => x.areas && x.areas.length)
        : null
    },
    visitBranchMenus: (state, getters) => {
      return state.settings && state.settings.system.bookingsEnabled.includes("Visit") &&
        state.settings.menus.branchMenus.some(x => x.visitCapacity > 0)
        ? state.settings.menus.branchMenus.filter(x => x.visitCapacity > 0)
        : null
    },
    visitBookingEnabled: (state, getters) => {
      return state.settings && state.settings.system.bookingsEnabled.includes("Visit")
    },
    gantryAccessQrEnabled: (state, getters) => {
      return state.settings && state.settings.system.bookingsEnabled.includes("GantryAccessQr")
    },
    currentBranchName: (state, getters) => { 

      const currentBranchCodeFromUrl = state.locationInfoFromUrl?.currentBranchCode
      const branchFound = state.settings.menus.branchMenus.find(x => x.code.toLowerCase() === currentBranchCodeFromUrl?.toLowerCase())

      return branchFound ? branchFound.name : constants.defaultBranchName
    },
  },
  mutations: {
    updateTime(state, payload) {
      // console.debug("updateTime", payload.now.toString(), payload.nowMoment.format(), payload.todayMoment.format())
      state.time = payload
    },
    updateSettings(state, payload) {
      state.settings = payload
    },
    updateAccountInfo(state, payload) {
      ////Abandon this cuz it can be solved with - this.$set(booking, 'canCheckInNowStatus', canCheckInNowStatus)

      //To fix Vue reactivity issue for new properties - No new properties
      //Add new properties if not found before assigning the object to Vue - canCancelNowStatus, canCheckInNowStatus, canExtendNowStatus
      // if (payload && payload.bookings && payload.bookings.length) {
      //   const foundProperty = Object.prototype.hasOwnProperty.call(payload.bookings[0], 'canCancelNowStatus');

      //   if (!foundProperty) {
      //     payload.bookings.forEach(x => {
      //       x.canCancelNowStatus = false;
      //       x.canCheckInNowStatus = false;
      //       x.canExtendNowStatus = false;
      //     });
      //   }
      // }
      state.accountInfo = payload
    },
    updateShowLoginPage(state, payload) {
      state.showLoginPage = payload
    },
    updateShowNavBar(state, payload) {
      state.showNavBar = payload
    },
    updateShowQRCodeBar(state, payload) {
      state.showQRCodeBar = payload
    },
    updateNavBarActiveButton(state, payload) {
      state.navBarActiveButton = payload
    },
    updateNavQRCodeActiveButton(state, payload) {
      state.navQRCodeActiveButton = payload
    },
    updateShowCheckIn(state, payload) {
      state.showCheckIn = payload
    },
    updateLoading(state, payload) {
      state.loading = payload
    },
    updateResult(state, payload) {
      state.result = payload
    },
    updateSystemDialog(state, payload) {
      state.systemDialog = payload
    },
    updateSystemAlert(state, payload) {
      state.systemAlert = payload
    },
    updateQRCode(state, payload) {
      state.qrCode = payload
    },
    updateLocationInfoFromQrCode(state, payload) {
      payload.time = new Date()
      state.locationInfoFromQrCode = payload
    },
    updateLocationInfoFromGps(state, payload) {
      payload.time = new Date()
      state.locationInfoFromGps = payload
    },
    // updateSid(state, payload) {
    //   state.sid = payload
    // },
    updateSearchResults(state, payload) {
      state.searchResults = payload
    },
    updateSelectedArea(state, payload) {
      state.selectedArea = payload
    },
    updateSelectedBooking(state, payload) {
      state.selectedBooking = payload
    },
    updateVisitBookingSearchResult(state, payload) {
      state.visitBookingSearchResult = payload
    },
    updatePreCasAuthState(state, payload) {
      state.preCasAuthState = payload
    },
    updateOtpTimer(state, payload) {
      state.otpTimer = payload
    },
    updateOtpTimerStartTime(state, payload) {
      state.otpTimerStartTime = payload
    },
    updateQrCodeValue(state, payload) {
      state.qrCodeEntry = payload
    },
    updateCasAuthState(state, payload) {
      state.casAuthState = payload
    },
    updateLocationInfoFromUrl(state, payload) {
      state.locationInfoFromUrl = payload
    }
  },
  actions: {
  },
  modules: {
  }
})
